import React, { useState } from 'react';
import { useDatabase } from '../../../db/DatabaseProvider';
import { useAuthStore } from '../../../stores';
import { Button } from '../../common/Button';
import { CustomSelect } from '../../common/CustomSelect';
import { useBranches } from '../../../db/hooks/useLookupData';
import type { CustomerDocType } from '../../../db/schemas';
import { Edit2, Save, X } from 'lucide-react';
import { now } from '../../../lib/uuid';
import { useToast } from '../../common/ToastProvider';

interface Props {
  customer?: CustomerDocType | null;
}

export function CustomerTabData({ customer }: Props) {
  const targetCustomer = customer;
  if (!targetCustomer) return null;

  const db = useDatabase();
  const user = useAuthStore(s => s.user);
  const isOwner = useAuthStore(s => s.isOwner);
  const { branches } = useBranches();
  const toast = useToast();
  
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    name: customer?.name || '',
    phone: customer?.phone || '',
    email: customer?.email || '',
    instagram: customer?.instagram || '',
    branch_id: customer?.branch_id || ''
  });

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!db || isSubmitting || !customer?.id) return;
    setIsSubmitting(true);
    try {
      const doc = await db.customers.findOne(customer.id).exec();
      if (doc) {
        await doc.patch({
          ...formData,
          updated_at: now(),
          last_modified_by: user?.email || 'system',
          
        });
        toast.success('Datos del cliente actualizados con éxito');
        setIsEditing(false);
      }
    } catch (e: any) {
      console.error(e);
      toast.error('Error al guardar datos: ' + (e.message || 'Error desconocido'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <h3 className="font-extrabold text-base text-text-primary">Información del Cliente</h3>
        {isOwner && (
          !isEditing ? (
            <Button variant="ghost" size="sm" icon={Edit2} onClick={() => setIsEditing(true)}>Editar Datos</Button>
          ) : (
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" icon={X} onClick={() => setIsEditing(false)}>Cancelar</Button>
              <Button variant="primary" size="sm" icon={Save} onClick={handleSave} isLoading={isSubmitting}>Guardar</Button>
            </div>
          )
        )}
      </div>

      <div className="bg-surface border border-border rounded-xl p-5 shadow-xs space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-text-secondary mb-1">Nombre Completo</label>
            {isEditing ? (
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm focus:border-brand-sky focus:ring-1 focus:ring-brand-sky outline-none text-text-primary"
              />
            ) : (
              <div className="text-sm font-medium text-text-primary py-2">{customer?.name || '-'}</div>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-text-secondary mb-1">Teléfono</label>
            {isEditing ? (
              <input
                type="text"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm focus:border-brand-sky focus:ring-1 focus:ring-brand-sky outline-none text-text-primary"
              />
            ) : (
              <div className="text-sm font-medium text-text-primary py-2">{customer?.phone || '-'}</div>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-text-secondary mb-1">Correo Electrónico</label>
            {isEditing ? (
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm focus:border-brand-sky focus:ring-1 focus:ring-brand-sky outline-none text-text-primary"
              />
            ) : (
              <div className="text-sm font-medium text-text-primary py-2">{customer?.email || '-'}</div>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-text-secondary mb-1">Instagram</label>
            {isEditing ? (
              <input
                type="text"
                value={formData.instagram}
                onChange={(e) => handleChange('instagram', e.target.value)}
                className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm focus:border-brand-sky focus:ring-1 focus:ring-brand-sky outline-none text-text-primary"
              />
            ) : (
              <div className="text-sm font-medium text-text-primary py-2">{customer?.instagram || '-'}</div>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-text-secondary mb-1">Sucursal Asociada</label>
            {isEditing && isOwner ? (
              <CustomSelect
                value={formData.branch_id}
                onChange={(val) => handleChange('branch_id', val)}
                options={branches.map(s => ({ value: s.id, label: s.name }))}
              />
            ) : (
              <div className="text-sm font-medium text-text-primary py-2">
                {branches.find(s => s.id === customer?.branch_id)?.name || 'Sede Global'}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
